import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { authenticateUser } from "../../../api/UserAPIUtils";
import { RootState } from "../../../app/store";
import { useAppDispatch } from "../../../hooks/useAppDispatch";
import { useAppSelector } from "../../../hooks/useAppSelector";
import AuthShell from "./AuthShell";
interface LoginWindowProps {
  setWindow: React.Dispatch<
    React.SetStateAction<"login" | "forgottenPassword" | "changePassword">
  >;
  redirectRoute: string;
}

const LoginWindow: React.FC<LoginWindowProps> = ({
  setWindow,
  redirectRoute,
}) => {
  const dispatch = useAppDispatch();
  const { loading, error, isAuthenticated, isPasswordResetRequired, token } =
    useAppSelector((state: RootState) => state.auth);
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: Location })?.from?.pathname || "/";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  useEffect(() => {
    if (isAuthenticated && token) {
      if (isPasswordResetRequired) {
        setWindow("changePassword");
      } else {
        navigate(from, { replace: true });
      }
    }
  }, [isAuthenticated, token, navigate]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const form = e.currentTarget;

    if (!form.checkValidity()) {
      return;
    }

    dispatch(authenticateUser(email, password));
  };

  const handleForgotPassword = () => {
    setWindow("forgottenPassword");
  };

  return (
    <AuthShell
      eyebrow="Welcome back"
      title="Sign in"
      description="Use your email and password to access your learning workspace."
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <label
            htmlFor="login-email"
            className="mb-2 block text-[11px] font-bold uppercase tracking-[0.22em] text-slate-700"
          >
            Email
          </label>
          <input
            id="login-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="h-14 w-full rounded-[20px] border border-slate-200 bg-white px-5 text-base text-slate-700 outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/15"
            placeholder="Enter your email"
          />
        </div>

        <div>
          <label
            htmlFor="login-password"
            className="mb-2 block text-[11px] font-bold uppercase tracking-[0.22em] text-slate-700"
          >
            Password
          </label>
          <input
            id="login-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className="h-14 w-full rounded-[20px] border border-slate-200 bg-white px-5 text-base text-slate-700 outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/15"
            placeholder="Enter your password"
          />
        </div>

        {error ? (
          <p role="alert" className="text-sm text-red-500">
            {error}
          </p>
        ) : null}

        <button
          type="submit"
          disabled={loading}
          className="inline-flex h-14 w-full items-center justify-center rounded-full bg-primary px-6 text-base font-semibold text-white shadow-[0_12px_24px_rgba(0,128,128,0.2)] transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {loading ? "Logging in..." : "Login"}
        </button>
      </form>

      <button
        type="button"
        onClick={handleForgotPassword}
        className="mt-5 text-sm font-semibold text-primary transition hover:opacity-80"
      >
        Forgot Password?
      </button>
    </AuthShell>
  );
};

export default LoginWindow;
